/**
 * UsagePage — credits-first view (Wave B).
 *
 * The pre-credits page surfaced subscription-style caps (0/25 cloud
 * runs, 0/10 test runs, 1 GB storage limit). None of those exist in
 * the credits model — every billable action just debits credits. So
 * this page now shows:
 *
 *   A. Balance hero  — current credit balance + Add credits CTA
 *   B. Window picker — 1d / 7d / 30d chip row
 *   C. Burn cards    — total spend in window, broken down by category
 *      (workflow runs, managed apps, AI proxy, warm pool, dedicated
 *      egress, other)
 *   D. Sparkline     — daily spend over the window
 *   E. Recent runs   — the same execution table the old page rendered,
 *      now bounded to the picked window and with a type filter +
 *      Export CSV that already worked
 *
 * Service `getUsage(window)` fetches everything in one call from
 * `/billing/info?detailed=true&window=<w>` — billing.js delegates that
 * to handlers/usage.getUsage which returns the credits-window shape.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IonSkeletonText, IonPopover } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import usageService from 'services/usage';
import Pagination, { usePagination } from 'components/atoms/Pagination';
import './UsagePage.css';

const EXECUTION_TYPES = [
  { key: 'all', label: 'All' },
  { key: 'analysis', label: 'Analysis' },
  { key: 'test', label: 'Test Run' },
  { key: 'workflow', label: 'Workflow' },
];

const TYPE_LABELS = EXECUTION_TYPES.reduce((acc, t) => {
  if (t.key !== 'all') acc[t.key] = t.label;
  return acc;
}, {});

const EXECUTION_ROUTES = {
  test: (exec) => `/projects/${exec.projectId}/runs/${exec.id}`,
  ticket_implementation: (exec) => `/projects/${exec.projectId}/implementations/${exec.id}`,
};

const WINDOWS = [
  { key: '1d', label: '1d' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
];

// Recent-executions table page size. The window can hold hundreds of
// runs; rendering them all at once bloats the DOM and the scroll. Paginate
// client-side (the data already arrives in one getUsage payload) — Export
// CSV still writes the full filtered set, not just the visible page.
const PAGE_SIZE = 25;

// Order + display labels for the burn-by-category cards. Anything that
// doesn't match a known key (the backend may add new categories before
// the frontend ships) drops into "Other".
const CATEGORY_LABELS = {
  workflow_run:     'Workflow runs',
  managed_app:      'Managed apps',
  warm_pool:        'Warm pool',
  dedicated_egress: 'Dedicated egress',
  other:            'Other',
};
const CATEGORY_ORDER = ['workflow_run', 'managed_app', 'warm_pool', 'dedicated_egress', 'other'];

// milliCents (1/100,000 of a USD) → human dollars, always 2 decimals so
// the page reads consistently regardless of magnitude. Sub-cent amounts
// (e.g. $0.0003 from a single managed-app-minute tick) collapse to
// `<$0.01` instead of `$0.00` so we don't silently round burns away.
// Matches industry convention (Stripe / AWS Cost Explorer): never show
// fractions of a cent in user-facing UI, but never round non-zero spend
// down to zero either.
const formatUsd = (mc) => {
  const n = Number(mc) || 0;
  const usd = n / 100000;
  if (usd === 0) return '$0.00';
  if (Math.abs(usd) < 0.005) return n < 0 ? '>-$0.01' : '<$0.01';
  return `$${usd.toFixed(2)}`;
};

const UsagePage = () => {
  const navigate = useNavigate();
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState('30d');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const loadUsage = useCallback(async () => {
    try {
      setLoading(true);
      const data = await usageService.getUsage(window);
      setUsageData(data);
    } catch (error) {
      console.error('Error loading usage:', error);
    } finally {
      setLoading(false);
    }
  }, [window]);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  const balanceMilliCents = usageData?.balance?.milliCents ?? 0;
  const burnTotalMilliCents = usageData?.burn?.totalMilliCents ?? 0;
  const burnByCategory = usageData?.burn?.byCategory ?? {};
  const burnDailyByCategory = usageData?.burn?.dailyByCategory ?? {};
  const dailyBurn = usageData?.burn?.daily ?? [];
  const filteredExecutions = useMemo(() => {
    const recentExecutions = usageData?.recentExecutions ?? [];
    return typeFilter === 'all'
      ? recentExecutions
      : recentExecutions.filter((e) => e.type === typeFilter);
  }, [usageData, typeFilter]);

  // Client-side pagination of the executions table. Reset to page 1 when
  // the filter or window changes (the row set is different).
  const {
    page: pageClamped,
    setPage,
    pageCount: totalPages,
    total: totalRows,
    pagedRows: pagedExecutions,
  } = usePagination(filteredExecutions, PAGE_SIZE, [typeFilter, window]);

  const maxDailySpend = dailyBurn.length
    ? Math.max(...dailyBurn.map((d) => d.milliCents), 1)
    : 1;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const formatModel = (exec) => {
    const agent = exec.agentType;
    const model = exec.model;
    if (agent && model) return `${agent} / ${model}`;
    if (agent) return agent;
    if (model) return model;
    return '-';
  };

  const handleExportCSV = () => {
    if (!filteredExecutions.length) return;
    const headers = ['Date', 'Type', 'Project', 'Status', 'Model'];
    const rows = filteredExecutions.map((exec) => [
      new Date(exec.date).toISOString(),
      TYPE_LABELS[exec.type] || exec.type,
      exec.projectName,
      exec.status,
      formatModel(exec),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `zibby-usage-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Helmet>
        <title>Usage - Teamy</title>
        <meta name="description" content="Track credit balance and per-category spend." />
      </Helmet>

      <div className="usage-page page-layout main-body">
        <div className="page-header-center">
          <h1>Usage</h1>
          <p>Track credit balance and spend by category.</p>
        </div>

        <div className="page-card-content">
          {/* ─── A. Balance hero + window picker ────────────────────── */}
          <div className="page-card">
            <div className="card-header">
              <h2>Balance &amp; spend</h2>
              <div className="usage-window-chips" role="tablist" aria-label="Time window">
                {WINDOWS.map((w) => (
                  <button
                    key={w.key}
                    type="button"
                    role="tab"
                    aria-selected={window === w.key}
                    className={`usage-filter-btn ${window === w.key ? 'active' : ''}`}
                    onClick={() => setWindow(w.key)}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="usage-balance-hero">
              <div className="usage-balance-block">
                <div className="total-label">Credit balance</div>
                {loading ? (
                  <IonSkeletonText animated style={{ width: '160px', height: '36px' }} />
                ) : (
                  <div className="total-amount">{formatUsd(balanceMilliCents)}</div>
                )}
              </div>
              <div className="usage-balance-block">
                <div className="total-label">Spend ({window})</div>
                {loading ? (
                  <IonSkeletonText animated style={{ width: '160px', height: '36px' }} />
                ) : (
                  <div className="total-amount">{formatUsd(burnTotalMilliCents)}</div>
                )}
              </div>
            </div>

            {/* ─── D. Daily spend sparkline ───────────────────────── */}
            <div className="usage-chart">
              <div className="chart-bars">
                {dailyBurn.map((day, index) => (
                  <div key={day.date} className="chart-bar-container">
                    <div
                      className="chart-bar"
                      style={{
                        height: `${(day.milliCents / maxDailySpend) * 100}%`,
                      }}
                      onMouseEnter={(e) => {
                        setHoveredBar({ date: day.date, milliCents: day.milliCents });
                        const rect = e.target.getBoundingClientRect();
                        setTooltipPosition({
                          x: rect.left + rect.width / 2,
                          y: rect.top - 10,
                        });
                      }}
                      onMouseLeave={() => setHoveredBar(null)}
                    />
                    {(index === 0 || index === dailyBurn.length - 1) && (
                      <div className="chart-date">{day.date.slice(5)}</div>
                    )}
                  </div>
                ))}
              </div>
              <div className="chart-baseline"></div>
            </div>

          </div>

          {hoveredBar && (
            <div
              className="chart-tooltip"
              style={{
                position: 'fixed',
                left: `${tooltipPosition.x}px`,
                top: `${tooltipPosition.y}px`,
                transform: 'translate(-50%, -100%)',
                zIndex: 1000,
                pointerEvents: 'none',
              }}
            >
              <div className="tooltip-content">
                <div className="tooltip-date">{hoveredBar.date}</div>
                <div className="tooltip-row">
                  <span className="tooltip-label">{hoveredBar.label || 'Spend'}</span>
                  <span className="tooltip-value">{formatUsd(hoveredBar.milliCents)}</span>
                </div>
              </div>
            </div>
          )}

          {/* ─── C. Burn breakdown by category — 2-col mini-card grid ── */}
          <div className="page-card">
            <div className="card-header">
              <div>
                <h2>Spend by category</h2>
                <p className="usage-category-subtitle">
                  {{ '1d': 'Last 24 hours', '7d': 'Last 7 days', '30d': 'Last 30 days' }[window] || `Last ${window}`}
                  {' · per-category daily spend'}
                </p>
              </div>
            </div>
            <div className="usage-category-grid-2col">
              {(() => {
                const formatShortDate = (yyyyMmDd) => {
                  if (!yyyyMmDd) return '';
                  const d = new Date(`${yyyyMmDd}T00:00:00Z`);
                  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                };
                const renderCard = (key, label) => {
                  const total = burnByCategory[key] || 0;
                  const series = burnDailyByCategory[key] || dailyBurn.map((d) => ({ date: d.date, milliCents: 0 }));
                  const maxMc = Math.max(...series.map((d) => d.milliCents), 1);
                  const firstDate = series[0]?.date;
                  const lastDate = series[series.length - 1]?.date;
                  // Empty cards still get a ghost timeline so the card has
                  // visual structure (date axis + tiny baseline ticks)
                  // instead of looking broken / loading. The CSS class
                  // swap controls the bar opacity + color.
                  const isEmpty = total === 0;
                  return (
                    <div key={key} className={`usage-category-card ${isEmpty ? 'is-empty' : ''}`}>
                      <div className="usage-category-card-header">
                        <span className="usage-category-card-title">{label}</span>
                        <span className="usage-category-card-total">
                          {loading ? (
                            <IonSkeletonText animated style={{ width: '60px', height: '14px' }} />
                          ) : (
                            formatUsd(total)
                          )}
                        </span>
                      </div>
                      <div className="usage-category-mini-chart">
                        {series.map((day) => {
                          // Zero-spend slots get a faint 2px "rest"
                          // baseline tick via the .is-zero modifier
                          // (drawn in CSS via ::before). Non-zero slots
                          // get a real bar with height proportional to
                          // this card's own max (so a $0.01 card and a
                          // $100 card both look visually meaningful).
                          const isZero = !day.milliCents;
                          const heightPct = isZero
                            ? 0
                            : Math.max((day.milliCents / maxMc) * 100, 4);
                          return (
                            <div
                              key={day.date}
                              className={`usage-category-mini-bar-slot ${isZero ? 'is-zero' : ''}`}
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setHoveredBar({
                                  date: day.date,
                                  milliCents: day.milliCents,
                                  label,
                                });
                                setTooltipPosition({
                                  x: rect.left + rect.width / 2,
                                  y: rect.top - 10,
                                });
                              }}
                              onMouseLeave={() => setHoveredBar(null)}
                            >
                              <div
                                className="usage-category-mini-bar"
                                style={{ height: `${heightPct}%` }}
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div className="usage-category-mini-dates">
                        <span>{formatShortDate(firstDate)}</span>
                        <span>{formatShortDate(lastDate)}</span>
                      </div>
                    </div>
                  );
                };
                return (
                  <>
                    {CATEGORY_ORDER.map((key) => renderCard(key, CATEGORY_LABELS[key]))}
                    {Object.entries(burnByCategory)
                      .filter(([k]) => !CATEGORY_ORDER.includes(k))
                      .map(([k]) => renderCard(k, k))}
                  </>
                );
              })()}
            </div>
          </div>

          {/* ─── E. Recent executions ───────────────────────────────── */}
          <div className="usage-table-card">
            <div className="usage-table-header">
              <div className="usage-table-header-left">Recent executions</div>
              <div className="usage-table-header-right">
                <button
                  id="type-filter-trigger"
                  className="usage-export-btn"
                  onClick={() => setIsTypeFilterOpen(true)}
                >
                  {EXECUTION_TYPES.find((t) => t.key === typeFilter)?.label || 'All'}
                </button>
                <IonPopover
                  trigger="type-filter-trigger"
                  isOpen={isTypeFilterOpen}
                  onDidDismiss={() => setIsTypeFilterOpen(false)}
                  showBackdrop={false}
                >
                  <div className="usage-type-filter-popover">
                    {EXECUTION_TYPES.map((t) => (
                      <div
                        key={t.key}
                        className={`menu-item ${typeFilter === t.key ? 'active' : ''}`}
                        onClick={() => {
                          setTypeFilter(t.key);
                          setIsTypeFilterOpen(false);
                        }}
                      >
                        {t.label}
                      </div>
                    ))}
                  </div>
                </IonPopover>
                <button className="usage-export-btn" onClick={handleExportCSV}>
                  Export CSV
                </button>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: '0' }}>
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="usage-skeleton-row">
                    <IonSkeletonText animated style={{ width: '120px', height: '16px' }} />
                    <IonSkeletonText animated style={{ width: '80px', height: '16px' }} />
                    <IonSkeletonText animated style={{ width: '100px', height: '16px' }} />
                    <IonSkeletonText animated style={{ width: '80px', height: '16px' }} />
                    <IonSkeletonText animated style={{ width: '140px', height: '16px' }} />
                  </div>
                ))}
              </div>
            ) : filteredExecutions.length > 0 ? (
              <table className="usage-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Project</th>
                    <th>Status</th>
                    <th>Model</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedExecutions.map((exec) => {
                    const routeFn = EXECUTION_ROUTES[exec.type];
                    return (
                      <tr
                        key={exec.id}
                        className={routeFn ? 'clickable-row' : ''}
                        onClick={() => routeFn && navigate(routeFn(exec))}
                      >
                        <td className="col-date">{formatDate(exec.date)}</td>
                        <td className="col-project">{TYPE_LABELS[exec.type] || exec.type}</td>
                        <td className="col-project">{exec.projectName}</td>
                        <td>
                          <span className="col-status">
                            <span className={`status-dot ${exec.status}`} />
                            {exec.status}
                          </span>
                        </td>
                        <td className="col-project">{formatModel(exec)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="usage-empty-state">
                <p>No executions in this window</p>
              </div>
            )}

            {!loading && (
              <Pagination
                page={pageClamped}
                pageCount={totalPages}
                total={totalRows}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UsagePage;
